import React, { useState, useEffect } from "react";
import LoginIcon from "@mui/icons-material/Login";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getCourses } from "../../services/CourseService";
import { toast } from "react-toastify";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import RightList from "./RightList";

const PriceButton = ({ children, isOwned }) => (
  <div className="flex items-center justify-between mt-6">
    <div className="text-[#db3b6d] font-extrabold text-2xl tracking-wide">
      {children}
    </div>
    <button className="w-12 h-12 flex items-center justify-center rounded-md bg-[#db3b6d] text-white shadow-md">
      {isOwned ? (
        <LoginIcon className="w-8 h-8" />
      ) : (
        <ShoppingCartOutlinedIcon className="w-8 h-8" />
      )}
    </button>
  </div>
);

const Pill = ({ label }) => (
  <div className="absolute -top-[55px] left-1/2 -translate-x-1/2 w-[110px] h-[110px] rounded-full bg-[#db3b6d] text-white flex items-center justify-center text-xl font-extrabold shadow-lg">
    {label}
  </div>
);

const OwnedTag = ({ isOwned }) => (
  <span
    className={`absolute -top-2 right-4 text-[10px] px-2 py-[2px] rounded-full ${
      isOwned
        ? "bg-[#f9dfe8] text-[#4b4b4b] border border-[#4b4b4b]"
        : "bg-[#db3b6d] text-white border-0"
    }`}
  >
    {isOwned ? "ĐÃ MUA" : "CHƯA MUA"}
  </span>
);

const CourseCard = ({ course }) => (
  <div className="relative rounded-md border-[3px] border-[#4b4b4b] p-6 pt-10 shadow-xl flex flex-col h-[450px] w-full max-w-[320px] mx-auto">
    <Pill label={course.type} />
    <OwnedTag isOwned={course.isOwned} />
    <h3 className="text-[#db3b6d] font-extrabold text-2xl text-center mb-1 mt-[70px]">
      {course.title}
    </h3>

    <div className="text-[#4b4b4b] leading-6 text-[15px] max-w-[290px] mx-auto flex-1 overflow-hidden">
      <p className="line-clamp-5">{course.description}</p>
    </div>
    <div className="h-[2px] bg-[#f1c0cf] rounded mt-6" />
    <PriceButton isOwned={course.isOwned}>
      ${" "}
      {new Intl.NumberFormat("vi-VN", {
        style: "currency",
        currency: "VND",
      }).format(course.price)}
    </PriceButton>
  </div>
);

const CoursesPage = () => {
  const [allCourses, setAllCourses] = useState([]); // Tất cả khóa học
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const coursesPerPage = 3;

  // Fetch all courses once
  useEffect(() => {
    fetchAllCourses();
  }, []);

  const fetchAllCourses = async () => {
    try {
      setLoading(true);
      // Lấy tất cả khóa học (limit rất lớn hoặc không limit)
      const response = await getCourses({
        page: 1,
        limit: 1000, // Số lớn để lấy tất cả
      });

      if (response?.data) {
        setAllCourses(response.data);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast.error("Không thể tải danh sách khóa học");
    } finally {
      setLoading(false);
    }
  };

  // Calculate pagination ở frontend
  const totalPages = Math.ceil(allCourses.length / coursesPerPage);
  const startIndex = (currentPage - 1) * coursesPerPage;
  const endIndex = startIndex + coursesPerPage;
  const currentCourses = allCourses.slice(startIndex, endIndex);

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage((p) => p - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
    }
  };

  const handlePageClick = (pageNum) => {
    setCurrentPage(pageNum);
  };

  return (
    <div className="min-h-screen bg-[#f9dfe8] pb-16">
      {/* Section title bar */}
      <div className="max-w-[96rem] mx-auto px-4">
        <div className="mt-4 inline-block bg-[#db3b6d] text-white px-6 py-3 rounded-r-md rounded-l-none font-bold shadow ml-[-100vw] pl-[100vw]">
          KHÓA HỌC
        </div>
      </div>

      <section className="max-w-[88rem] mx-auto px-4 mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8">
          {/* Left column: cards + pagination */}
          <div>
            <div
              className={`relative ${totalPages > 1 ? "px-12 md:px-16" : ""}`}
            >
              {/* Prev on left */}
              {totalPages > 1 && (
                <button
                  onClick={handlePrev}
                  disabled={currentPage === 1}
                  className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-[#f1c0cf] hover:bg-[#db3b6d] text-white shadow absolute left-0 top-1/2 -translate-y-1/2 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Trang trước"
                >
                  <ChevronLeft />
                </button>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 items-stretch justify-items-center">
                {loading ? (
                  // Loading skeleton
                  [...Array(3)].map((_, idx) => (
                    <div
                      key={idx}
                      className="relative rounded-md border-[3px] border-[#4b4b4b] p-6 pt-10 shadow-[0_8px_20px_rgba(219,59,109,0.15)] flex flex-col h-full w-full max-w-[320px] mx-auto animate-pulse"
                    >
                      <div className="absolute -top-[55px] left-1/2 -translate-x-1/2 w-[110px] h-[110px] rounded-full bg-gray-300" />
                      <div className="h-6 bg-gray-300 rounded w-3/4 mx-auto mt-[70px] mb-3" />
                      <div className="h-4 bg-gray-300 rounded w-1/2 mx-auto mb-4" />
                      <div className="space-y-2 flex-1">
                        <div className="h-4 bg-gray-300 rounded w-full" />
                        <div className="h-4 bg-gray-300 rounded w-full" />
                        <div className="h-4 bg-gray-300 rounded w-3/4" />
                      </div>
                    </div>
                  ))
                ) : currentCourses.length > 0 ? (
                  currentCourses.map((course) => (
                    <CourseCard key={course._id} course={course} />
                  ))
                ) : (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    Chưa có khóa học nào
                  </div>
                )}
              </div>

              {/* Next on right */}
              {totalPages > 1 && (
                <button
                  onClick={handleNext}
                  disabled={currentPage === totalPages}
                  className="hidden md:flex items-center justify-center w-12 h-12 rounded-full bg-[#f1c0cf] hover:bg-[#db3b6d] text-white shadow absolute right-0 top-1/2 -translate-y-1/2 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Trang sau"
                >
                  <ChevronRight />
                </button>
              )}
            </div>

            {/* Dots only */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-6">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handlePageClick(i + 1)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      currentPage === i + 1
                        ? "bg-[#db3b6d]"
                        : "border-2 border-gray-400"
                    }`}
                    aria-label={`Trang ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Right list on large screens */}
          <div className="hidden lg:block justify-self-end">
            <RightList />
          </div>
        </div>

        {/* Right list on small screens (below dots) */}
        <div className="mt-6 flex justify-center lg:hidden">
          <RightList />
        </div>
      </section>
    </div>
  );
};

export default CoursesPage;
